import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { brandHeroGradient } from '../theme/styleHelpers';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  FormHelperText,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { authService } from '../services';

/**
 * Reset Password Page
 *
 * Allows users to set a new password using a reset token from email
 */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const muiTheme = useMuiTheme();

  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extract token from URL query params
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  // Password validation rules
  const validateLowercase = (password: string): boolean => /[a-z]/.test(password);
  const validateUppercase = (password: string): boolean => /[A-Z]/.test(password);
  const validateNumber = (password: string): boolean => /\d/.test(password);
  const validateSpecialChar = (password: string): boolean => /[@$!%*?&~]/.test(password);
  const validateLength = (password: string): boolean => password.length >= 8;

  const isPasswordValid =
    validateLowercase(password) &&
    validateUppercase(password) &&
    validateNumber(password) &&
    validateSpecialChar(password) &&
    validateLength(password);

  const passwordStrength = [
    validateLength(password),
    validateLowercase(password),
    validateUppercase(password),
    validateNumber(password),
    validateSpecialChar(password)
  ].filter(Boolean).length;

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return 'error';
    if (passwordStrength <= 4) return 'warning';
    return 'success';
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', valid: validateLength(password) },
    { label: 'One lowercase letter', valid: validateLowercase(password) },
    { label: 'One uppercase letter', valid: validateUppercase(password) },
    { label: 'One number', valid: validateNumber(password) },
    { label: 'One special character (@$!%*?&~)', valid: validateSpecialChar(password) },
  ];

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid reset token. Please request a new password reset link.');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (!isPasswordValid) {
      setError('Please ensure your password meets all the criteria');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, password);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err.message?.includes('expired') || err.message?.includes('invalid')) {
        setError('This reset link has expired or is invalid. Please request a new password reset.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
      {/* Brand Logo/Header */}
      <Box
        component={Link}
        to="/"
        sx={{
          mt: { xs: 4, sm: 6 },
          mb: 4,
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <Typography
          variant="h3"
          component="div"
          sx={{
            fontWeight: 800,
            background: brandHeroGradient(muiTheme),
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}
        >
          Tier One Content
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.75rem' }}
        >
          Premium Content Sharing Platform
        </Typography>
      </Box>

      {/* Reset Password Form */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        {!success ? (
          <>
            <Typography component="h1" variant="h5" gutterBottom>
              Set New Password
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Choose a strong password for your account
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
                {error.includes('expired') && (
                  <Box sx={{ mt: 1 }}>
                    <Link to="/forgot-password" style={{ color: 'inherit', fontWeight: 'bold' }}>
                      Request a new reset link
                    </Link>
                  </Box>
                )}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              {/* New Password Field */}
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onInput={(e: React.FormEvent<HTMLDivElement>) => {
                  const target = e.target as HTMLInputElement;
                  setPassword(target.value);
                }}
                disabled={loading || !token}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={loading || !token}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Strength Indicator */}
              {password && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Password Strength
                    </Typography>
                    <Typography
                      variant="caption"
                      color={`${getPasswordStrengthColor()}.main`}
                      fontWeight="bold"
                    >
                      {passwordStrength === 5 ? 'Strong' : passwordStrength >= 3 ? 'Medium' : 'Weak'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(passwordStrength / 5) * 100}
                    color={getPasswordStrengthColor()}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )}

              {/* Password Requirements */}
              {password && (
                <Box sx={{ mb: 2 }}>
                  <FormHelperText>Password must contain:</FormHelperText>
                  {passwordRequirements.map((req, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {req.valid ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 1 }} />
                      )}
                      <Typography
                        variant="caption"
                        color={req.valid ? 'success.main' : 'text.secondary'}
                      >
                        {req.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Confirm Password Field */}
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onInput={(e: React.FormEvent<HTMLDivElement>) => {
                  const target = e.target as HTMLInputElement;
                  setConfirmPassword(target.value);
                }}
                disabled={loading || !token}
                error={confirmPassword.length > 0 && password !== confirmPassword}
                helperText={
                  confirmPassword.length > 0 && password !== confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPasswordVisibility}
                        edge="end"
                        disabled={loading || !token}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !token || !isPasswordValid || password !== confirmPassword}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  to="/login"
                  style={{
                    textDecoration: 'none',
                    color: mode === 'dark' ? '#00d4aa' : '#00a884',
                  }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography component="h1" variant="h5" gutterBottom>
                Password Reset Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your password has been reset successfully.
                <br />
                Redirecting you to the login page...
              </Typography>

              <CircularProgress size={32} sx={{ mb: 2 }} />

              <Link
                to="/login"
                style={{ textDecoration: 'none', width: '100%' }}
              >
                <Button variant="outlined" fullWidth>
                  Go to Login Now
                </Button>
              </Link>
            </Box>
          </>
        )}
      </Paper>

      {/* Additional Help */}
      {!success && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Need help?{' '}
            <Link
              to="/help"
              style={{
                color: mode === 'dark' ? '#00d4aa' : '#00a884',
                textDecoration: 'none',
              }}
            >
              Contact Support
            </Link>
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ResetPassword;
